import { useEffect, useRef, useState } from 'react'
import {
  getUrlSearchParameter,
  platformStoreDelete,
  platformStoreSet,
  stripHttpProtocol,
} from '../../../../shared/helpers'
import {
  checkDeviceId,
  getActivationStatus,
  getFavoriteStation,
} from '../../actions/contentActions'
import { registerUser } from '../../actions/userActions'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import googleAnalytics4 from '../../lib/googleAnalytics'
import { DEVICE_KEY, getActivationRedirectLink, UID_KEY } from './utils'
import { useNavigate } from 'react-router'
import Platform from '../../platforms'

export function useActivationCode() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.user)
  const [vppaModalVisibility, setVppaModalVisibility] = useState<boolean>(false)
  const timerResets = useRef<NodeJS.Timeout[]>([])

  const stopPolling = () => {
    timerResets.current.forEach((timer) => clearTimeout(timer))
    timerResets.current = []
  }

  useEffect(() => stopPolling, [])

  const pollActivation = () => {
    //polling activation status
    getActivationStatus()
      .then((resp) => {
        //if we have the expected result, clear interval and navigate to home

        if (resp.errorCode === 0) {
          //retrieve UID and store it
          if (resp && resp.device && resp.device.UID) {
            platformStoreSet(UID_KEY, resp.device.UID)

            googleAnalytics4.setUserProperty('signed_in_user', resp.device.UID)
            //we have a UID, store in Redux
            dispatch({
              type: 'UPDATE_DEVICE',
              payload: { UID: resp.device.UID },
            })
            registerUser(resp.device.UID).then(() => {
              getFavoriteStation(resp.device.UID).then((localStationResp) => {
                //receive the response from the API and process
                dispatch({
                  type: 'UPDATE_STATION',
                  payload: localStationResp,
                })
                const localCallSign = localStationResp.call_sign

                googleAnalytics4.setUserProperty(
                  'station_localization',
                  localCallSign
                )

                //Reset/Nullify Home Content so Home page can refresh when it mounts
                dispatch({
                  type: 'SET_HOME_CONTENT',
                  payload: { collections: null },
                })

                registerUser(resp.device.UID).then(() => {
                  // Stop polling
                  stopPolling()

                  if (
                    user.has_memberships &&
                    (!user.vppa_accepted ||
                      user.vppa_status === 'never_accepted' ||
                      user.vppa_status === 'expired')
                  ) {
                    // Display VPPA Agreement Modal
                    setVppaModalVisibility(true)
                  } else {
                    //navigate to target
                    const redirectValue = getUrlSearchParameter(
                      location.search,
                      'redirect'
                    )

                    if (redirectValue) {
                      let link = stripHttpProtocol(redirectValue)
                      link += '?fromDeeplink=true'
                      window.deeplink = ''
                      navigate(link)
                    } else {
                      navigate('/home')
                    }
                  }
                })
                Platform.signIn(localCallSign)
              })
            })
          }
        } else if (resp.errorCode === 500005) {
          //polling completed, error code 500005
          //expire code,return user to activation screen
          platformStoreDelete(DEVICE_KEY)
          checkDeviceId()
          //navigate to target
          navigate(getActivationRedirectLink(location.search))
        }
      })
      .catch((err) => {
        // brought to the attention of ops that 500 is not the correct status
        // code for this response
        console.warn(err)
      })
  }

  const navigateHome = () => {
    if (user.pid) {
      navigate('/home')
    }
  }

  return {
    pollActivation,
    timerResets,
    user,
    vppaModalVisibility,
    setVppaModalVisibility,
    navigateHome,
  }
}
